"use client"
// ds-color-ignore-file — Figma 473:2359's white frame under the photo, the
// dim over the blurred photo, and the shade that keeps white text legible on
// the sharp one; the DS has no tokens for photo scrims.

/**
 * 05b — What you get, the page's immersive section (Eugene, 29.09): the
 * logic of 05, "Everything lands in one plan" (`home-v4-one-plan.tsx`, off
 * the page since), with Figma 473:2359's five tiles
 * (`home-v4-immersive-tiles.tsx`) in place of its collage.
 *
 * Two beats, as in 05. First the heading and the lede over the sharp photo.
 * Then, as you keep scrolling, the photo blurs into Figma's frame and the
 * tiles fly in, the first row into the lede's place. 05 sized its collage to
 * the viewport; these tiles span the text column, as the text does, so the
 * section is as tall as they are. Its top stays pinned for as long as the two
 * beats take, then the rest scrolls up.
 *
 * The mechanics are 05's:
 *   1. The blur is not animated. The photo lies in four copies, sharp and
 *      three blur steps, and each fades out over the next, so no frame
 *      changes a filter. Only two neighbouring copies show at once, so the
 *      eye sees the blur grow rather than a sharp photo over a smear.
 *   2. Progress goes to CSS custom properties from a rAF loop, never to React
 *      state, so nothing re-renders per frame.
 *   3. The stage eases after the scrollbar instead of jumping with it, and a
 *      scroll that goes quiet mid-beat finishes that beat on its own.
 * Under 1024 px, and with reduced motion, nothing is pinned: the lede stays in
 * the flow above the tiles, and under 1024 each tile below the fold rises in
 * once as it comes into view.
 */

import Image from "next/image"
import { useCallback, useEffect, useRef } from "react"
import { blobMediaUrl } from "@/lib/blob-url"
import { useHomeV4Copy } from "./home-v4-copy"
import { HomeV4ImmersiveTiles } from "./home-v4-immersive-tiles"
import {
  VpnKeyIcon,
  HealthAndSafetyIcon,
  LockPersonIcon,
} from "./home-v4-figma"

// Figma 473:2361 "image 284": the photo under a 24 % black fill, layer blur
// 79.3. The page blurs it about half as much, so it reads as a photo out of
// focus rather than a smear (Eugene, 29.09, on the full blur: low quality).
const BG = blobMediaUrl(
  "/images/marketing/home-v4/immersive/section-bg-daylight-v7.webp"
)

const TRUST_ICONS = {
  vpn: VpnKeyIcon,
  shield: HealthAndSafetyIcon,
  lock: LockPersonIcon,
}

/**
 * Where each beat sits along the pin, 0→1. 05 held the sharp photo for 6% of
 * a 120svh pin, about 60px, so a reader who stopped just past the heading
 * had the idle settle blur it outright (Eugene, 13.10: "why blur straight
 * away"). Now the sharp photo holds for about a third of a screen of a longer
 * pin, and the tiles keep roughly their old pace.
 */
const CFG = {
  /** Beat one holds the sharp photo; the blur cross-fade runs here. */
  blurStart: 0.2,
  blurEnd: 0.4,
  /** Beat two: the tiles arrive; their stagger is in the tiles' CSS. */
  revealStart: 0.38,
  revealEnd: 0.86,
}

/** The photo's copies, bottom to top: three blur steps, then the sharp one. */
const PHOTO_LAYERS = ["wyg-soft-3", "wyg-soft-2", "wyg-soft-1", "wyg-sharp"]

/** 05's time constants, ms; see `home-v4-one-plan.tsx`. */
const TAU = { scrub: 110, settle: 220, blur: 160, release: 90 }

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

export function HomeV4WhatYouGet({ section }: { section: string }) {
  const trackRef = useRef<HTMLElement>(null)
  /** The pin's length: an empty block that the pinned top scrolls past. */
  const dwellRef = useRef<HTMLDivElement>(null)
  const { WHAT_YOU_GET: copy } = useHomeV4Copy()

  // 05's stage state: what is shown, where it is heading and at what pace,
  // and which beats have settled on their own.
  const shown = useRef({ t: 1, reveal: 1 })
  const target = useRef({ t: 1, reveal: 1 })
  const settled = useRef({ blur: false, reveal: false })
  const tau = useRef(TAU.scrub)
  const loopRaf = useRef(0)
  const lastFrame = useRef(0)
  const idleTimer = useRef(0)

  const render = useCallback((t: number, reveal: number) => {
    const el = trackRef.current
    if (!el) return
    shown.current = { t, reveal }
    el.style.setProperty("--blur", `${t}`)
    el.style.setProperty("--reveal", `${reveal}`)
  }, [])

  /** Beat progress straight from the scrollbar, before any settling. */
  const rawProgress = useCallback(() => {
    const el = trackRef.current
    if (!el) return null
    // No pin under 1024 px or with reduced motion: the end state.
    const dwell = dwellRef.current?.offsetHeight ?? 0
    const p = dwell > 0 ? clamp01(-el.getBoundingClientRect().top / dwell) : 1
    return {
      t: clamp01((p - CFG.blurStart) / (CFG.blurEnd - CFG.blurStart)),
      reveal: clamp01(
        (p - CFG.revealStart) / (CFG.revealEnd - CFG.revealStart)
      ),
    }
  }, [])

  /** One frame of the ease towards `target`; keeps itself going until it lands. */
  const tick = useCallback(
    (now: number) => {
      const dt = Math.min(64, now - (lastFrame.current || now))
      lastFrame.current = now
      const k = 1 - Math.exp(-dt / tau.current)
      const s = shown.current
      const g = target.current
      let t = s.t + (g.t - s.t) * k
      let reveal = s.reveal + (g.reveal - s.reveal) * k
      const landed =
        Math.abs(g.t - t) < 0.002 && Math.abs(g.reveal - reveal) < 0.002
      if (landed) {
        t = g.t
        reveal = g.reveal
        lastFrame.current = 0
      }
      render(t, reveal)
      loopRaf.current = landed ? 0 : requestAnimationFrame(tick)
    },
    [render]
  )

  /** Point the stage at `g` and ease there with time constant `ms`. */
  const moveTo = useCallback(
    (g: { t: number; reveal: number }, ms: number) => {
      target.current = g
      tau.current = ms
      if (!loopRaf.current) {
        lastFrame.current = 0
        loopRaf.current = requestAnimationFrame(tick)
      }
    },
    [tick]
  )

  const applyFromScroll = useCallback(() => {
    const raw = rawProgress()
    if (!raw) return
    const s = settled.current
    // Back at a beat's start: hand it to the scrollbar again, easing down
    // rather than snapping out of the settled state.
    const released = (s.reveal && raw.reveal < 0.02) || (s.blur && raw.t < 0.02)
    if (s.reveal && raw.reveal < 0.02) s.reveal = false
    if (s.blur && raw.t < 0.02) s.blur = false
    const want = { t: s.blur ? 1 : raw.t, reveal: s.reveal ? 1 : raw.reveal }
    if (released) {
      moveTo(want, TAU.release)
      return
    }
    // A settle owns the stage until it lands.
    if (loopRaf.current && tau.current > TAU.scrub) return
    moveTo(want, TAU.scrub)
  }, [rawProgress, moveTo])

  /**
   * A reader who stops mid-beat is not left with a half-blurred photo or
   * half-faded tiles (Eugene, 04.09): once the scroll goes quiet, the beat
   * finishes on its own.
   */
  const onIdle = useCallback(() => {
    const raw = rawProgress()
    if (!raw) return
    const s = settled.current
    if (!s.reveal && raw.reveal > 0 && raw.reveal < 1) {
      s.reveal = true
      s.blur = true
      moveTo({ t: 1, reveal: 1 }, TAU.settle)
      return
    }
    if (!s.blur && raw.t > 0 && raw.t < 1 && raw.reveal === 0) {
      s.blur = true
      moveTo({ t: 1, reveal: target.current.reveal }, TAU.blur)
    }
  }, [rawProgress, moveTo])

  useEffect(() => {
    // Read once, as 05 does: a hook that reports `false` on the first render
    // would play the reveal before correcting itself.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      render(1, 1)
      return
    }
    // Start where the page is rather than easing in from the end state.
    const raw = rawProgress()
    if (raw) {
      target.current = raw
      render(raw.t, raw.reveal)
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
      window.clearTimeout(idleTimer.current)
      idleTimer.current = window.setTimeout(onIdle, 220)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
      cancelAnimationFrame(loopRaf.current)
      loopRaf.current = 0
      window.clearTimeout(idleTimer.current)
    }
  }, [applyFromScroll, onIdle, render, rawProgress])

  useEffect(() => {
    // Under 1024 px nothing is pinned, so a tile below the fold rises in once
    // as it comes into view. Tiles already on screen are left as they are.
    if (
      window.innerWidth >= 1024 ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("IntersectionObserver" in window)
    )
      return
    const tiles = Array.from(
      trackRef.current?.querySelectorAll<HTMLElement>(".imt-tile") ?? []
    ).filter(
      (tile) => tile.getBoundingClientRect().top > window.innerHeight * 0.92
    )
    if (!tiles.length) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          entry.target.removeAttribute("data-wait")
          io.unobserve(entry.target)
        }
      },
      { rootMargin: "0px 0px -8% 0px" }
    )
    for (const tile of tiles) {
      tile.setAttribute("data-wait", "")
      io.observe(tile)
    }
    return () => {
      io.disconnect()
      for (const tile of tiles) tile.removeAttribute("data-wait")
    }
  }, [])

  return (
    <section id="what-you-get" ref={trackRef} className="wyg">
      <div className="wyg-bg" aria-hidden="true">
        <div className="wyg-scene">
          {PHOTO_LAYERS.map((layer) => (
            <div key={layer} className={`wyg-photo ${layer}`}>
              <Image
                src={BG}
                alt=""
                fill
                sizes="105vw"
                className="object-cover"
              />
            </div>
          ))}
          <div className="wyg-shade" />
          <div className="wyg-dim" />
          <div className="wyg-grain" />
        </div>
      </div>
      <div className="wyg-pin">
        <div className={`${section} wyg-content`}>
          <header className="wyg-heading">
            <p className="type-label-lg">{copy.eyebrow}</p>
            <h2 className="type-display-md-emphasized mt-3">
              {copy.heading.plain}
              <br />
              {copy.heading.accent}
            </h2>
          </header>
          <div className="wyg-body">
            <p className="wyg-lede type-body-xl">{copy.lede}</p>
            <HomeV4ImmersiveTiles />
          </div>
          <ul className="wyg-trust">
            {copy.trust.map((item) => {
              const Icon = TRUST_ICONS[item.icon]
              return (
                <li key={item.text}>
                  <Icon className="size-7 shrink-0 text-white" />
                  <p className="wyg-trust-label">{item.text}</p>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
      <div ref={dwellRef} className="wyg-dwell" aria-hidden="true" />
      <style>{CSS}</style>
    </section>
  )
}

const CSS = `
  /* --blur runs 0 → 1 as the photo goes out of focus; --wyg-soft is the
     last step's radius. It follows the photo's drawn size: the frame's
     width on a wide screen and, on a tall one, the height the photo is cut
     to. */
  .wyg{--blur:1;--reveal:1;--wyg-soft:max(1.2vw,1.55lvh);position:relative;width:100vw;margin-inline:calc(50% - 50vw);background:white}
  /* The photo spans the section; its sticky frame is one viewport tall. */
  .wyg-bg{position:absolute;inset:0;pointer-events:none}
  .wyg-scene{position:sticky;top:0;height:100lvh;overflow:hidden}
  /* Figma's photo box overhangs the 1584 × 1221 frame by 186 on top and 251
     below. The sides overhang three blur radii, so no edge thins onto the
     white; the photo is cut to its height, so the crop stays the same. */
  .wyg-photo{position:absolute;inset:-15.23% calc(var(--wyg-soft) * -3) -20.56%;will-change:opacity}
  /* Three blur steps, each twice the last, saturating as they go so the
     photo stays in colour instead of greying. The upper of two neighbours
     fades over the lower, which is fully opaque: at 0 the sharp photo, by
     1/3 the first step, by 2/3 the second, at 1 the last. */
  .wyg-soft-3{filter:blur(var(--wyg-soft)) saturate(1.4);opacity:clamp(0, calc((var(--blur) * 3 - 2) * 60), 1)}
  .wyg-soft-2{filter:blur(calc(var(--wyg-soft) / 2)) saturate(1.25);opacity:min(clamp(0, calc((var(--blur) * 3 - 1) * 60), 1), clamp(0, calc(3 - var(--blur) * 3), 1))}
  .wyg-soft-1{filter:blur(calc(var(--wyg-soft) / 4)) saturate(1.1);opacity:min(clamp(0, calc(var(--blur) * 180), 1), clamp(0, calc(2 - var(--blur) * 3), 1))}
  .wyg-sharp{opacity:clamp(0, calc(1 - var(--blur) * 3), 1)}
  /* Nothing moves under 1024 px or with reduced motion: the last step alone. */
  .wyg-soft-2,.wyg-soft-1,.wyg-sharp{display:none}
  /* Beat one's shade, under the heading and the lede, hands over to the
     blurred photo's even 26 % dim. */
  .wyg-shade{position:absolute;inset:0;opacity:calc(1 - var(--blur));background:linear-gradient(180deg,rgba(20,16,14,.5) 0%,rgba(20,16,14,.28) 40%,rgba(20,16,14,0) 65%),linear-gradient(90deg,rgba(20,16,14,.3) 0%,rgba(20,16,14,0) 55%)}
  .wyg-dim{position:absolute;inset:0;opacity:var(--blur);background:rgba(0,0,0,.26)}
  /* Fine grain, as film has: it breaks up the banding a smooth 8-bit
     gradient shows on large screens. Light noise over the whole background,
     sharp or blurred (Eugene, 13.10), blended soft-light as on the close
     section, so it textures the photo without greying it. */
  .wyg-grain{position:absolute;inset:0;opacity:.32;mix-blend-mode:soft-light;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")}
  .wyg-pin{position:relative}
  .wyg-content{padding-top:48px;padding-bottom:48px}
  .wyg-heading{color:white}
  .wyg-heading>p:first-child{opacity:.65}
  .wyg-body{position:relative;margin-top:32px}
  .wyg-lede{max-width:520px;margin-bottom:32px;color:white}
  .wyg-dwell{display:none}
  .wyg-trust{display:flex;justify-content:center;gap:36px;color:white;opacity:.9}
  .wyg-trust li{display:flex;align-items:flex-start;gap:11px}
  .wyg-trust-label{width:162px;font-size:12.5px;line-height:16.25px;letter-spacing:.25px;font-weight:500;text-transform:uppercase}
  .wyg-trust li:nth-child(odd) .wyg-trust-label{width:154px}
  @media (min-width:1024px){
    .wyg-content{padding-top:88px;padding-bottom:84px}
    .wyg-body{margin-top:40px}
  }
  /* 1024 and up: the top of the section stays pinned while the two beats
     play, for 05's length (a 220vh track round a 100vh stage). */
  @media (min-width:1024px) and (prefers-reduced-motion:no-preference){
    .wyg-soft-2,.wyg-soft-1,.wyg-sharp{display:block}
    .wyg-pin{position:sticky;top:0}
    .wyg-dwell{display:block;height:160svh}
    /* The lede holds the first row's place until the tiles come, and clears
       before the first of them is fully in (05's 0.35). */
    .wyg-lede{position:absolute;top:0;left:0;margin:0;opacity:clamp(0, calc(1 - var(--reveal) / .35), 1)}
    .wyg-trust{opacity:calc(clamp(0, calc((var(--reveal) - .45) / .4), 1) * .9)}
  }
  @media (max-width:1023px){.wyg-trust{flex-wrap:wrap;gap:20px;margin-top:40px}}
  @media (max-width:600px){.wyg-trust{flex-direction:column;justify-content:flex-start;gap:24px}}
`
